import { useState } from 'react';
import { Button, Input, Label, Modal } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useStoreApiKey, useStoreAppCredential } from '@/hooks/useIdentityCredentials';
import { apiKeySchemaFor } from './apiKeySchemas';
import { hasAppCredentialFlow } from './identity-lib';
import type { ProviderSummary } from '@/types/identity';
import type { Scope } from './scope';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  provider: ProviderSummary;
  scope: Scope;
}

/**
 * Add-API-key modal driven by `apiKeySchemaFor(providerId)` and the catalog's
 * `apiKeySetupInstructions`. When the catalog declares `apiKeyHeaders` (e.g.
 * Datadog), the modal renders one input per header and submits them as a
 * `headers` map; otherwise it renders the single `apiKey` field. Identity
 * returns HTTP 422 with a provider-curated `message` when the secret is
 * invalid — render that message verbatim so the user sees the right
 * diagnostic.
 */
export default function AddApiKeyModal({ isOpen, onClose, provider, scope }: Props) {
  const { success } = useToast();
  const store = useStoreApiKey();
  const storeApp = useStoreAppCredential();
  const schema = apiKeySchemaFor(provider.id);

  const headerSpecs = provider.apiKeyHeaders ?? [];
  const isMultiHeader = headerSpecs.length > 0;
  // Providers whose bot/app token is a separate credential (Slack) store the
  // org-scope token via PUT /org/credentials/app rather than the org
  // service-account endpoint. Space scope uses the normal space api-key route.
  const isAppCredential = hasAppCredentialFlow(provider);

  const [apiKey, setApiKey] = useState('');
  const [headers, setHeaders] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<Record<string, string>>({});
  const [displayName, setDisplayName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reset = () => {
    setApiKey('');
    setHeaders({});
    setConfig({});
    setDisplayName('');
    setSubmitError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const trimmedConfig = Object.keys(config).length ? config : undefined;
    const trimmedDisplay = displayName.trim() || undefined;

    try {
      if (scope.kind === 'org' && isAppCredential) {
        // Slack-style bot token: register as an org app credential so agents can
        // act "as the bot". Single-value token only (app-credential providers
        // don't declare multi-header schemas).
        await storeApp.mutateAsync({
          provider: provider.id,
          authType: 'api_key',
          apiKey,
          displayName: trimmedDisplay,
        });
      } else if (scope.kind === 'org') {
        await store.mutateAsync({
          scope: 'org',
          body: {
            provider: provider.id,
            config: trimmedConfig,
            displayName: trimmedDisplay,
            secret: isMultiHeader
              ? { authType: 'api_key', headers }
              : { authType: 'api_key', apiKey },
          },
        });
      } else {
        // user and space scopes share the same `ApiKeyStoreRequest`
        // body shape — only the target endpoint differs.
        const apiKeyBody = isMultiHeader
          ? {
              provider: provider.id,
              headers,
              config: trimmedConfig,
              displayName: trimmedDisplay,
            }
          : {
              provider: provider.id,
              apiKey,
              config: trimmedConfig,
              displayName: trimmedDisplay,
            };
        await store.mutateAsync(
          scope.kind === 'user'
            ? { scope: 'user', body: apiKeyBody }
            : { scope: { spaceId: scope.spaceId }, body: apiKeyBody },
        );
      }
      success(`${provider.displayName} connected`);
      handleClose();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Could not store API key');
    }
  };

  const secretReady = isMultiHeader
    ? headerSpecs.every((h) => (headers[h.name] ?? '').trim().length > 0)
    : apiKey.trim().length > 0;
  const configReady = schema.configFields.every(
    (f) => !f.required || (config[f.name] ?? '').trim().length > 0,
  );
  const canSubmit = secretReady && configReady;
  const isPending = store.isPending || storeApp.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Connect ${provider.displayName}`}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={isPending}>
            Connect
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {provider.apiKeySetupInstructions && (
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {provider.apiKeySetupInstructions}
          </p>
        )}

        {isMultiHeader ? (
          headerSpecs.map((header) => (
            <div key={header.name} className="space-y-1.5">
              <Label htmlFor={`header-${header.name}`}>{header.label}</Label>
              <Input
                id={`header-${header.name}`}
                type={header.secret ? 'password' : 'text'}
                value={headers[header.name] ?? ''}
                onChange={(e) =>
                  setHeaders((prev) => ({ ...prev, [header.name]: e.target.value }))
                }
                autoComplete="off"
              />
            </div>
          ))
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="apiKey">{schema.apiKey.label}</Label>
            <Input
              id="apiKey"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={schema.apiKey.placeholder}
              autoComplete="off"
            />
            {schema.apiKey.helpText && (
              <p className="text-xs text-muted-foreground">{schema.apiKey.helpText}</p>
            )}
          </div>
        )}

        {schema.configFields.map((field) => (
          <div key={field.name} className="space-y-1.5">
            <Label htmlFor={`field-${field.name}`}>{field.label}</Label>
            <Input
              id={`field-${field.name}`}
              type={field.type ?? 'text'}
              value={config[field.name] ?? ''}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, [field.name]: e.target.value }))
              }
              placeholder={field.placeholder}
            />
            {field.helpText && (
              <p className="text-xs text-muted-foreground">{field.helpText}</p>
            )}
          </div>
        ))}

        <div className="space-y-1.5">
          <Label htmlFor="displayName">Display name (optional)</Label>
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Personal, Sales Inbox"
          />
          <p className="text-xs text-muted-foreground">
            Shown in the credential list. Renameable later.
          </p>
        </div>

        {submitError && (
          <p className="text-sm text-status-error-fg" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
}
